// Phase QCM-EIN — canonical EIN formatting + validation helpers.
//
// FMCSA returns EIN as an integer (e.g. 208148260). The app's display
// contract is always ``XX-XXXXXXX`` (2 digits, dash, 7 digits).
// Comparison contract is digit-only — so a customer-typed "20-8148260"
// matches an FMCSA-returned 208148260.
//
// Inputs accepted: integer, string, mixed alpha (stripped), spaces,
// dots, dashes. We intentionally don't auto-pad short values — that
// would mask data-entry mistakes.

/**
 * Strip everything that isn't a digit and return the resulting
 * string. ``null``/``undefined``/non-string-or-number inputs return
 * the empty string.
 */
export function normalizeEin(input) {
    if (input === null || input === undefined) return "";
    const s = typeof input === "number" ? String(input) : (typeof input === "string" ? input : "");
    return s.replace(/\D/g, "");
}

/**
 * ``true`` only when input contains exactly 9 digits after stripping
 * non-digits. Mid-type values (8 or fewer digits) are NOT valid yet
 * — callers use this on blur/submit, not on every keystroke.
 */
export function isValidEin(input) {
    return normalizeEin(input).length === 9;
}

/**
 * Format any EIN-shaped input to ``XX-XXXXXXX``. When the input
 * contains exactly 9 digits, format with the dash. Otherwise return
 * the ORIGINAL string unchanged so the user's mid-type value isn't
 * mangled.
 *
 * Examples:
 *   formatEin(208148260)         // "20-8148260"
 *   formatEin("208148260")       // "20-8148260"
 *   formatEin("20-8148260")      // "20-8148260"
 *   formatEin(" 20 8148260 ")    // "20-8148260"
 *   formatEin("20.8148260")      // "20-8148260"
 *   formatEin("2081482")         // "2081482"   (passthrough)
 *   formatEin("ABC")             // "ABC"       (passthrough)
 *   formatEin(null)              // ""
 *   formatEin(undefined)         // ""
 */
export function formatEin(input) {
    if (input === null || input === undefined) return "";
    if (typeof input !== "string" && typeof input !== "number") return "";
    const original = typeof input === "number" ? String(input) : input;
    const digits = normalizeEin(input);
    if (digits.length === 9) {
        return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return original;
}

/**
 * Mask helper used by the form input ``onChange`` handler — accepts
 * any string the user typed/pasted, returns a display value that:
 *   - drops non-digits / non-dash characters,
 *   - caps at 9 digits (so an extra paste doesn't run past the mask),
 *   - inserts the dash after the 2nd digit once we have ≥3 digits.
 *
 * Mid-type values (1–8 digits) keep growing naturally. This lets
 * the input feel like a typeahead mask without a heavy library.
 */
export function maskEinInput(value) {
    if (value === null || value === undefined) return "";
    const digits = String(value).replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

/**
 * Digit-only equality check for the "Verified from FMCSA" badge on
 * the EIN field specifically. Non-EIN fields continue to use the
 * exact-string ``isVerifiedFromFmcsa`` helper.
 */
export function isEinVerifiedFromFmcsa(formValue, fmcsaValue) {
    const a = normalizeEin(formValue);
    const b = normalizeEin(fmcsaValue);
    if (a.length !== 9 || b.length !== 9) return false;
    return a === b;
}
